import { PropsWithChildren, useCallback, useMemo } from "react";
import { Claims, UserTypes } from "../../models";
import { useProfile } from "..";
import { IPermissionsContext, PermissionsContext } from ".";

export const PermissionsProvider = (props: PropsWithChildren) => {
    const { userSummary } = useProfile();

    const userPermissions = useMemo(() => userSummary?.permissions ?? [], [userSummary?.permissions]);
    const userRoles = useMemo(() => userSummary?.roles ?? [], [userSummary?.roles]);
    const authorizedTenants = useMemo(() => userSummary?.authorizedTenants ?? [], [userSummary?.authorizedTenants]);

    const hasRole = useCallback((role: UserTypes) => userRoles.some((r) => r === role), [userRoles]);

    const hasAnyRole = useCallback((roles: UserTypes[]) => roles.some((r) => hasRole(r)), [hasRole]);

    const hasPermission = useCallback(
        (permission: Claims) => userPermissions.some((p) => p === permission),
        [userPermissions]
    );

    const hasAnyPermission = useCallback((claims: Claims[]) => claims.some((c) => hasPermission(c)), [hasPermission]);

    const isGuest = useMemo<boolean>(
        () => (hasRole(UserTypes.Guest) && userRoles.length === 1) || !userRoles.length,
        [hasRole, userRoles.length]
    );

    const isSurgeryUser = useMemo(() => hasAnyRole([UserTypes.Surgeon, UserTypes.SurgeryScheduler]), [hasAnyRole]);

    const isGlobalAdmin = useMemo(() => hasRole(UserTypes.GlobalAdmin), [hasRole]);

    const hasSuperAdminAccess = useMemo<boolean>(
        () => isGlobalAdmin || hasRole(UserTypes.SuperAdmin),
        [hasRole, isGlobalAdmin]
    );

    const value = useMemo<IPermissionsContext>(
        () => ({
            authorizedTenants,
            userPermissions,
            userRoles,
            hasRole,
            hasAnyRole,
            hasPermission,
            hasAnyPermission,
            isGuest,
            isSurgeryUser,
            isGlobalAdmin,
            hasSuperAdminAccess,
        }),
        [
            authorizedTenants,
            userPermissions,
            userRoles,
            hasRole,
            hasAnyRole,
            hasPermission,
            hasAnyPermission,
            isGuest,
            isSurgeryUser,
            isGlobalAdmin,
            hasSuperAdminAccess,
        ]
    );

    return <PermissionsContext.Provider value={value} {...props} />;
};
